import { ButtonCus } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Textarea } from '@/components/common/Textarea';
import { DATE_YMD_HMS } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { ReservationNote } from '@/types/reservation';
import { formatDateTime } from '@/utils/datetime';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import NoteHistory from '../modal/NoteHistory';

export default observer(function Notes() {
    // hooks
    const { t } = useTranslation();
    const { id } = useParams();

    // store
    const {
        modalStore: { showAlertModal, showModal },
        reservationStore: { getReservationCMSDetail, updateReservation }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        note: yup
            .string()
            .trim().required('form_error_validate.required')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            note: ''
        }
    });

    // function
    const onSubmit = async (data: FormData) => {
        const payload = {
            id: Number(id), note: data.note
        };
        const res = await flowResult(updateReservation(payload));
        if (res) {
            showAlertModal({
                id: 'add-success',
                type: 'success',
                content: t('messages.add_success'),
                onClickXIcon: () => {
                    form.reset();
                    getReservationCMSDetail({ id });
                }
            });
        } else {
            showAlertModal({
                id: 'update-fail',
                type: 'error',
                content: t('messages.add_failure')
            });
        }
    };

    const handleClickShowNoteHistory = () => {
        if (id) {
            showModal({
                id: 'bike-note',
                size: '2xl',
                title: t('words_title.note_history'),
                titleClassName: 'text-center w-full',
                footerClassName: 'p-0',
                content: (
                    <NoteHistory id={id} />
                )
            });
        }
    };

    return (
        <div className='w-full'>
            <div className='flex justify-between items-center gap-2'>
                <h1 className='text-fs-mid18 text-black-1 font-semibold'>{t('words_title.note')}</h1>
                <ButtonCus
                    variant='default'
                    size='md'
                    className='h-9.5 md:h-12 flex gap-2 font-medium'
                    onClick={handleClickShowNoteHistory}
                >
                    {t('button.show_note_history')}
                </ButtonCus>
            </div>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className='mt-4'>
                    <div>
                        <FormField
                            control={form.control}
                            name='note'
                            render={({ field }) => (
                                <FormItem>
                                    {/* <FormLabel>{t('words_title.note')}</FormLabel> */}
                                    <FormControl>
                                        <Textarea {...field} rows={3} maxLength={500} placeholder={t('placeholder.enter')}></Textarea>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div>
                        <div className='mt-4 flex justify-center items-center gap-4'>
                            <ButtonCus variant='default' size='md' type='submit'>{t('button.save')}</ButtonCus>
                        </div>
                    </div>
                </form>
            </Form>
        </div>
    );
});

interface NoteItemProps {
    note: ReservationNote
}
export const BookingNoteItem = ({ note }: NoteItemProps) => {
    // hooks
    const { t } = useTranslation('transaltion', { keyPrefix: 'field' });

    return (
        <div className='flex flex-col gap-2'>
            <p className='bg-background-10 p-3 rounded-md font-normal text-[#1A1D1F] whitespace-pre-line break-all'>
                {note?.content}
            </p>
            <div className='flex justify-end gap-2'>
                <label>
                    {t('reservation.note_time')}
                    :
                </label>
                <p>{note?.noteTime && formatDateTime(note.noteTime, DATE_YMD_HMS)}</p>
                <p>{note?.email}</p>
            </div>
        </div>
    );
};
